import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { ago, moneyShort } from "@/lib/format";
import { useShop, useTill } from "@/lib/till-context";
import { ROLE_LABEL, type StaffLite, type StaffRole } from "@/lib/types";
import { Avatar, Button, Field, Input, Modal, Select, cx, useToast } from "@/components/ui";
import { Card, PageHead, td, th } from "@/components/Layout";
import { StaffPerformance } from "@/components/Insights";

const COLORS = ["#f59e0b", "#99f6e4", "#fda4af", "#bfdbfe", "#d9f99d", "#e9d5ff", "#fed7aa"];

export function Staff() {
  const qc = useQueryClient();
  const { me, refetchMe } = useTill();
  const shop = useShop();
  const q = useQuery({ queryKey: ["staff"], queryFn: () => api<{ staff: StaffLite[] }>(till("/admin/staff")) });
  const [edit, setEdit] = useState<StaffLite | "new" | null>(null);
  const s = me?.settings;
  const fmtLimit = (v: number | null | undefined) => (v == null ? "Any" : `Up to ${moneyShort(v)}`);
  const perms: [string, string, string, string][] = [
    ["Sell & take payment", "Yes", "Yes", "Yes"],
    ["Give a discount", `Up to ${s?.discountLimit.cashier ?? 3}%`, `Up to ${s?.discountLimit.supervisor ?? 10}%`, "Any"],
    ["Change a price at the till", "No", "Yes", "Yes"],
    ...(shop.serials ? ([["Sell without scanning the IMEI", "No", "Yes, picks from stock", "Yes"]] as [string, string, string, string][]) : []),
    ["Refund", fmtLimit(s?.refundLimit.cashier), fmtLimit(s?.refundLimit.supervisor), "Any"],
    ["Paid out from the drawer", "No", "Yes", "Yes"],
    ["Approve over / short at close", "No", "Yes", "Yes"],
    ["Link a new barcode / quick-add product", "No", "Yes", "Yes"],
    ["Receive, transfer & adjust stock", "No", "Yes", "Yes"],
    ["Staff, settings & tills", "No", "Reports only", "Yes"],
  ];
  return (
    <>
      <PageHead title="Staff & roles" sub="Till staff sign in with a 4-digit PIN · they don't get access to the website admin">
        <Button tone="primary" onClick={() => setEdit("new")}>
          Add staff
        </Button>
      </PageHead>
      <div className="flex flex-col gap-4">
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {["Name", "Role", "PIN", "Last on", "Sales this week", ""].map((h) => (
                    <th key={h} className={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(q.data?.staff ?? []).map((p) => (
                  <tr key={p.id} className={cx(!p.active && "opacity-50")}>
                    <td className={td}>
                      <span className="flex items-center gap-2.5">
                        <Avatar name={p.name} color={p.color} size={30} />
                        <b>{p.name}</b>
                      </span>
                    </td>
                    <td className={td}>{ROLE_LABEL[p.role]}</td>
                    <td className={td}>{p.hasPin ? "Set" : <span className="text-amber-700">Not set</span>}</td>
                    <td className={cx(td, "text-slate-500")}>{ago(p.lastSeenAt)}</td>
                    <td className={cx(td, "font-mono")}>{moneyShort(p.week ?? 0)}</td>
                    <td className={td}>
                      <button type="button" onClick={() => setEdit(p)} className="text-[13px] font-semibold text-brand">
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <StaffPerformance />
        <Card title="What each role can do">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {["Permission", "Cashier", "Supervisor", "Manager"].map((h) => (
                    <th key={h} className={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perms.map(([n, a, b, c]) => (
                  <tr key={n}>
                    <td className={cx(td, "font-semibold")}>{n}</td>
                    <td className={td}>{a}</td>
                    <td className={td}>{b}</td>
                    <td className={td}>{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-5 py-3 text-[13px] text-slate-500">Over a limit, the till asks for a supervisor's or manager's PIN on the spot and records who approved it. Limits are set in Settings.</p>
        </Card>
      </div>
      {edit ? <StaffModal person={edit === "new" ? null : edit} onClose={() => setEdit(null)} onSaved={() => { void qc.invalidateQueries({ queryKey: ["staff"] }); refetchMe(); }} /> : null}
    </>
  );
}

function StaffModal({ person, onClose, onSaved }: { person: StaffLite | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(person?.name ?? "");
  const [role, setRole] = useState<StaffRole>(person?.role ?? "cashier");
  const delivery = !!useTill().me?.settings.delivery?.enabled;
  const [pin, setPin] = useState("");
  const [color, setColor] = useState(person?.color ?? COLORS[Math.floor(Math.random() * COLORS.length)]!);
  const [active, setActive] = useState(person?.active ?? true);
  const save = async () => {
    try {
      const body: Record<string, unknown> = { name, role, color, active };
      if (pin) body.pin = pin;
      await api(till(person ? `/admin/staff/${person.id}` : "/admin/staff"), { method: person ? "PUT" : "POST", body });
      toast(person ? "Saved" : `${name} added`);
      onSaved();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    }
  };
  return (
    <Modal title={person ? `Edit ${person.name}` : "Add staff"} onClose={onClose} footer={<Button tone="primary" onClick={save} disabled={!name || (!person && pin.length !== 4) || (pin.length > 0 && pin.length !== 4)}>Save</Button>}>
      <div className="grid gap-3">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
        </Field>
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value as StaffRole)}>
            <option value="cashier">Cashier: sells, small discounts and refunds</option>
            <option value="supervisor">Supervisor: approvals, stock, reports</option>
            <option value="manager">Manager: everything, staff and settings</option>
            {delivery || role === "yard" ? <option value="yard">Yard staff: only the Yard screen (loading and sending out deliveries)</option> : null}
          </Select>
        </Field>
        <Field label={person ? "New PIN (leave blank to keep)" : "4-digit PIN"} hint="Each person's PIN must be different">
          <Input inputMode="numeric" type="password" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className="font-mono text-lg tracking-[0.4em]" />
        </Field>
        <div>
          <span className="text-[13px] font-semibold">Colour</span>
          <div className="mt-1.5 flex gap-2">
            {COLORS.map((c) => (
              <button key={c} type="button" aria-label={c} onClick={() => setColor(c)} style={{ background: c }} className={cx("h-9 w-9", color === c && "ring-2 ring-slate-900 ring-offset-2")} />
            ))}
          </div>
        </div>
        {person ? (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="h-5 w-5 accent-[var(--brand)]" /> Can sign in
          </label>
        ) : null}
      </div>
    </Modal>
  );
}
